

















































































import {
  defineComponent,
  ref,
  computed,
  useRouter,
  useContext,
  useRoute,
  watch,
  useFetch,
  onUpdated,
  onUnmounted,
} from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { debounce } from 'lodash-es';
import sanitizeHtml from 'sanitize-html';
import urlJoin from 'url-join';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import ContentArchivesSideMenu from '~/components/partials/Content/ContentArchivesSideMenu.vue';
import ContentDynamicComponent from '~/components/partials/Content/ContentDynamicComponent.vue';
import ContentProductList from '~/components/partials/Content/ContentProductList.vue';
import ContentRelatedContents from '~/components/partials/Content/ContentRelatedContents.vue';
import ContentRelatedLinks from '~/components/partials/Content/ContentRelatedLinks.vue';
import ContentSearchInput from '~/components/partials/Content/ContentSearchInput.vue';
import ContentTagsAndShareIcons from '~/components/partials/Content/ContentTagsAndShareIcons.vue';
import ContentLayout from '~/components/partials/Layout/ContentLayout.vue';
import { useContentParts, ContentParts } from '~/composables/contents/useContentParts';
import { useRequestBody } from '~/composables/contents/useRequestBody';
import { useSearchProductsParam } from '~/composables/contents/useSearchProductsParam';
import { useSilverEggApi } from '~/composables/silverEgg/useSilverEggApi';
import { useUuidEncryption } from '~/composables/silverEgg/useUuidEncryption';
import { useKeepScroll } from '~/composables/useKeepScroll';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useRuntimeConfig } from '~/composables/useRuntimeConfig';
import { useStore } from '~/composables/useStore';
import {
  PRODUCT_SEARCH_SORT_OPTIONS,
  PRODUCT_LIST_PER_PAGE,
  ARCHIVES_SIDE_MENU,
  PRODUCT_LIST_MAP,
  FIRST_SIZE,
  PRODUCT_ORDER_NUMBER_THRESHOLD,
  PRODUCT_SALE_THRESHOLD,
  DYNAMIC_PARTS_NAME,
  SPEC,
  REQUEST_NUM,
  CATEGORY_ID,
  SMARTPHONE,
} from '~/constants';
import { url, karteTrack, nonNullable } from '~/utils';
import { getArchiveAreaCategoryText, getArchiveColorCategoryText } from '~/utils/archive';
import { PageError } from '~/utils/error';
import { ProductSearch } from '~/types/modules/contents';
import { ProductListItem } from '~/types/modules/product';
import { ProductSearchSortOrder, ArticleType } from '~/enums';

export default defineComponent({
  components: {
    ContentDynamicComponent,
    AppImage,
    AppLoader,
    AppSeeMoreButton,
    ContentArchivesSideMenu,
    ContentTagsAndShareIcons,
    ContentLayout,
    ContentSearchInput,
    ContentProductList,
    ContentRelatedLinks,
    ContentRelatedContents,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { FRONTEND_ORIGIN } = useRuntimeConfig();
    const data = ref<any>();
    const { createQuery, createSortParam } = useSearchProductsParam();
    const { parseRequestBody } = useRequestBody();
    const { fetchRecommendItems } = useSilverEggApi();
    const { encryptUuid } = useUuidEncryption();
    const { i18n, req, app } = useContext();
    const { fetchContentParts } = useContentParts();
    const { scrollToPreviousPosition, setPageData } = useKeepScroll();

    // 入力中の文字
    const searchString = ref('');
    // 商品データ
    const products = ref<Array<ProductListItem>>([]);
    // 在庫あるかどうか
    const hasStock = ref(store.getters['internals/keepScroll/hasStock'](route.value.path, route.value.fullPath));
    // 商品一覧項目のindex
    const currentSortIndex = ref(
      store.getters['internals/keepScroll/sortIndex'](route.value.path, route.value.fullPath)
    );
    // 商品一覧項目
    const productSearchCondition = ref<ProductSearch | null>(null);
    // 商品一覧項目のページネーション
    const pagination = ref(store.getters['internals/keepScroll/pagination'](route.value.path, route.value.fullPath, 0));
    // 商品一覧の合計値
    const totalNumber = ref(0);
    // 暗号化したuuid
    const uuidHash = ref<string | undefined>('');
    // 関連コンテンツなどのパーツ
    const parts = ref<ContentParts>();
    // ダイナミックパーツのDOM参照
    const dynamicPartsWrapperRef = ref<HTMLDivElement | null>(null);
    // プレビュー表示かどうか（管理画面経由）
    const isPreview = ref<boolean>(req && req.method === 'POST');

    const isMobile = computed(() => app.$ua.deviceType() === SMARTPHONE);

    // クエリがあればクエリを除いた canonical を返す
    const canonical = computed(() => {
      const { path, query } = route.value;

      if (Object.keys(query).length > 0) {
        return path;
      }
    });

    const isItemsReady = computed<boolean>(
      () => !(showProductList.value && totalNumber.value > 0) || products.value.length > 0
    );

    onUpdated(() => {
      if (dynamicPartsWrapperRef.value) {
        const scrollObserver = new MutationObserver(
          debounce((mutationsList, observer) => {
            scrollToPreviousPosition(500);
            observer.disconnect();
          }, 500)
        );
        scrollObserver.observe(dynamicPartsWrapperRef.value, { attributes: true, childList: true, subtree: true });
        dynamicPartsWrapperRef.value.id = 'dynamic-parts-wrapper'; // HACK: 変更を検知させるために属性を付与
      }
    });

    onUnmounted(() => {
      setPageData({
        pagination: [pagination.value], // 0:商品一覧（ContentProductList）
        detailsElements: [{ elementId: 'content-related-links' }], // 0:関連リンク
        hasStock: hasStock.value,
        sortIndex: currentSortIndex.value,
      });
    });

    const { fetchState } = useFetch(async () => {
      let permalink = '';
      let previewData = null;
      // プレビューの表示
      if (isPreview.value) {
        const reqBody = await parseRequestBody(req);
        if (!reqBody) throw new PageError(404, {});

        previewData = reqBody;
        permalink = previewData.permalink;
      } else {
        permalink = route.value.params.permalink;
      }

      if (!permalink) throw new PageError(404, {});

      parts.value = await fetchContentParts(ArticleType.Archives, permalink, previewData);
      data.value = parts.value.data;

      try {
        await fetchProducts();
      } catch {
        throw new PageError(404, {});
      }
    });

    watch(
      () => data.value?.article_id,
      async () => {
        if (isPreview.value) return;

        window.TLAB.push(['Beacon::PutAccessLog', [`article_${data.value.article_id}`]]);

        // シルバーエッグのレコメンド枠リクエスト&トラッキング
        if (app.$ua.isFromCrawler()) return;
        uuidHash.value = await encryptUuid();
        await fetchRecommendItems({
          prod: [data.value.article_id], // トラッキング用
          spec: isMobile.value ? SPEC.ARTICLE_DETAIL_SP : SPEC.ARTICLE_DETAIL_PC, // 読み物と共用
          num: REQUEST_NUM,
          cat: CATEGORY_ID.SPECIAL_FEATURE,
          cust: uuidHash.value,
        });
      }
    );

    /**
     * 商品一覧取得
     */
    const fetchProducts = async () => {
      const { product_search_condition } = data.value?.contents;

      productSearchCondition.value = product_search_condition;

      if (!productSearchCondition.value) return;

      const { q, hasStockQuery } = await createQuery(
        productSearchCondition.value,
        !productSearchCondition.value.is_search_exclusion_display,
        hasStock.value
      );

      hasStock.value = hasStockQuery.value;

      const option = PRODUCT_SEARCH_SORT_OPTIONS[currentSortIndex.value];

      if (!option) {
        return;
      }

      const { target, order } = option;

      const { sort_condition_1, sort_condition_2, sort_condition_3, sort_condition_4, sort_condition_5 } =
        productSearchCondition.value;

      // 初期パラメータ
      const resParams = [sort_condition_1, sort_condition_2, sort_condition_3, sort_condition_4, sort_condition_5]
        .map((item) => {
          const column = item?.column;
          const direction = item?.direction;

          if (!column || !direction) return null;

          if (column === 'product_special_price') {
            const key = direction.toLowerCase();
            return key === ProductSearchSortOrder.ASC ? PRODUCT_LIST_MAP[column].asc : PRODUCT_LIST_MAP[column].desc;
          }

          if (column === 'producing_area') {
            return `area_id_1_i ${direction.toLowerCase()},area_id_2_i ${direction.toLowerCase()},area_id_3_i ${direction.toLowerCase()}`;
          }

          if (column === 'size') {
            return `abs(sub(${PRODUCT_LIST_MAP[column]}, ${FIRST_SIZE})) ${direction.toLowerCase()}`;
          }

          if (column.includes('product_order_number') && direction.toLowerCase() === ProductSearchSortOrder.ASC) {
            return `if(exists(${PRODUCT_LIST_MAP[column]}), ${
              PRODUCT_LIST_MAP[column]
            }, ${PRODUCT_ORDER_NUMBER_THRESHOLD}) ${direction.toLowerCase()}`;
          }

          if (
            ['product_sale_from', 'product_sale_to'].includes(column) &&
            direction.toLowerCase() === ProductSearchSortOrder.ASC
          ) {
            return `if(exists(${PRODUCT_LIST_MAP[column]}), ${
              PRODUCT_LIST_MAP[column]
            }, ${PRODUCT_SALE_THRESHOLD}) ${direction.toLowerCase()}`;
          }

          return `${PRODUCT_LIST_MAP[column]} ${direction.toLowerCase()}`;
        })
        .filter(nonNullable);

      const sort = createSortParam(target, order, resParams.join(','), ArticleType.Archives);

      const { items, total } = await store.dispatch('modules/product/fetchProducts', {
        q,
        sort,
        pagination: pagination.value,
      });

      products.value = items;
      totalNumber.value = total;
    };

    watch(
      [hasStock, currentSortIndex, pagination],
      debounce(async () => await fetchProducts(), 100)
    );

    // 商品一覧の表示・非表示
    const showProductList = computed(() => !!productSearchCondition.value && !!productSearchCondition.value.enable);

    // もっと見るボタンの表示・非表示
    const isLoadMoreButtonVisible = computed(() => totalNumber.value > pagination.value * PRODUCT_LIST_PER_PAGE);

    // 構造化マークアップで使用するリードテキスト
    const leadText = computed((): string | undefined => {
      if (!parts.value) return '';
      const [part] = parts.value.dynamicParts;

      try {
        if (part?.parts === 'body_text') {
          return sanitizeHtml(part.items.text || '', { allowedTags: [] });
        }
      } catch {
        // なにも返さないがハンドリングもしない
      }
    });

    const isEnabledNoIndex = computed((): boolean => {
      return !data.value?.contents.search_index;
    });

    /**
     * フリーワードで検索
     */
    const searchWords = async () => {
      await router.push({
        path: url('ARCHIVES_LIST'),
        query: {
          _title: searchString.value ? searchString.value : undefined,
        },
      });
    };

    /**
     * カテゴリ検索
     * @param query クエリ
     */
    const searchCategory = async (category: string, type: string) => {
      // 全ての特集
      if (!category && !type) {
        await router.push({
          path: url('ARCHIVES_LIST'),
        });
      }

      if (type === ARCHIVES_SIDE_MENU.area) {
        await router.push({
          path: url('ARCHIVES_LIST'),
          query: {
            area: category,
          },
        });

        return;
      }

      await router.push({
        path: url('ARCHIVES_LIST'),
        query: {
          wine_type: category,
        },
      });
    };

    // KARTE
    const unsubscribe = watch(
      data,
      async (data) => {
        if (isPreview.value || process.server || !data) {
          return;
        }
        // 一回実行されたら停止
        unsubscribe();
        const tags: string[] = [];
        try {
          const { result } = await store.dispatch('modules/content/fetchTags', {
            tags: data.contents?.tags || [],
          });
          tags.push(...(result?.map((item) => item?.contents?.tag_name)?.filter(nonNullable) || []));
        } catch {
          // 何もしない
        }
        const publishAt = data.publish_at || null;
        const expireAt = data.expire_at || null;
        const [area1, area2, area3] = getArchiveAreaCategoryText(data.contents?.producing_district);
        const color = getArchiveColorCategoryText(data.contents?.type);
        karteTrack('_view_category', {
          category_num: products.value.length,
          category_id: data.permalink,
          category_name: data.title,
          category_url: urlJoin(FRONTEND_ORIGIN, route.value.fullPath),
          page_type: i18n.t('Content-02.kartePageType').toString(),
          open_from_dtime: dayjs(publishAt).isValid()
            ? dayjs(publishAt).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          open_to_dtime: dayjs(expireAt).isValid()
            ? dayjs(expireAt).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          area1,
          area2,
          area3,
          producer: '',
          color,
          tags,
        });
      },
      { immediate: true }
    );

    useLayoutProps(() => ({
      fetchState,
      layout: {
        headerMenu: 'never', // ContentLayoutの中で設置
        topBanner: 'needed',
      },
      head: {
        title: data.value?.title,
        description: data.value?.contents?.meta_description || undefined, // どちらも入力がない場合は、nullが返らないようにundefinedを入力
        noindex: isEnabledNoIndex.value,
        canonical: canonical.value,
      },
      breadcrumbs: [
        { title: i18n.t('Content-02.listTitle').toString(), to: url('ARCHIVES_LIST') },
        { title: data.value?.title, to: null },
      ],
      og: {
        image: data.value?.contents?.main_image_pc,
        description: data.value?.contents?.meta_description || undefined, // どちらも入力がない場合は、nullが返らないようにundefinedを入力
      },
      twitter: {
        card: 'summary_large_image',
      },
      jsonld: [
        {
          type: 'BlogPosting',
          data: {
            url: url('ARCHIVES_DETAIL', { permalink: data.value?.permalink }),
            title: data.value?.title || '',
            images: [data.value?.contents?.main_image_pc].filter((image) => !!image),
            publishedAt: data.value?.publish_at || '',
            updatedAt: data.value?.update_at,
            description: leadText.value,
          },
        },
      ],
    }));

    return {
      parts,
      fetchState,
      data,
      searchString,
      searchWords,
      searchCategory,
      url,
      products,
      currentSortIndex,
      hasStock,
      productSearchCondition,
      isLoadMoreButtonVisible,
      pagination,
      totalNumber,
      isEnabledNoIndex,
      showProductList,
      DYNAMIC_PARTS_NAME,
      isItemsReady,
      dynamicPartsWrapperRef,
    };
  },
});
